import type { AliasProblem } from '../../shared/aliasFilter'
import { apiFetch } from '~/utils/api'

/**
 * 👤 The player's alias (v6.43). `alias` is undefined until the server
 * has been asked, null when the account has none yet. The picker opens
 * once per session for a real account with no name; "Later" closes it
 * and the boards show the Player-#### fallback until they pick one.
 *
 * v6.45: the profile also carries the TITLE worn (an achievement id)
 * and the AVATAR picked (a creature id) — set from the Profile hub,
 * shown on the account chip and on every board row.
 */
export type AliasError = AliasProblem | 'taken' | 'cooldown' | 'offline'
export type IdentityError = 'unknown' | 'locked' | 'offline'

/**
 * The refusal code a route put in its 409 — read from the JSON BODY.
 * v6.43 read `error.statusMessage`, which ofetch fills from the HTTP
 * status text; production speaks HTTP/2, which has no status text, so
 * every legitimate refusal ("taken", "cooldown") came back as '' and
 * the picker said "could not reach the server". The body carries the
 * same code on every protocol.
 */
export function refusalCode(error: unknown): string | null {
  const e = error as { statusCode?: number; statusMessage?: string; data?: { statusMessage?: unknown } } | null
  if (e?.statusCode !== 409) return null
  const fromBody = typeof e.data?.statusMessage === 'string' ? e.data.statusMessage : ''
  const code = fromBody || e.statusMessage || ''
  return code ? code : null
}

export function useProfile() {
  const alias = useState<string | null | undefined>('profile-alias', () => undefined)
  const renameAt = useState<number>('profile-rename-at', () => 0)
  const title = useState<string | null>('profile-title', () => null)
  const avatar = useState<string | null>('profile-avatar', () => null)
  const pickerOpen = useState<boolean>('alias-picker-open', () => false)
  const dismissed = useState<boolean>('alias-picker-dismissed', () => false)

  async function authed(): Promise<Record<string, string> | null> {
    const current = (await firebaseReady()).auth.currentUser
    if (!current || current.isAnonymous) return null
    return { Authorization: `Bearer ${await current.getIdToken()}`, ...(await appCheckHeader()) }
  }

  async function load(): Promise<void> {
    const headers = await authed()
    if (!headers) { alias.value = undefined; return }
    try {
      const p = await apiFetch<{ alias: string | null; renameAt: number; title?: string | null; avatar?: string | null }>('/api/profile', { headers })
      alias.value = p.alias
      renameAt.value = p.renameAt
      title.value = p.title ?? null
      avatar.value = p.avatar ?? null
    } catch (error) {
      console.warn('Profile load failed.', error)
    }
  }

  /** After a real sign-in: learn the alias, and ask for one if there is none. */
  async function afterSignIn(): Promise<void> {
    await load()
    if (alias.value === null && !dismissed.value) pickerOpen.value = true
  }

  async function save(name: string): Promise<AliasError | null> {
    const headers = await authed()
    if (!headers) return 'offline'
    try {
      const res = await apiFetch<{ ok: boolean; alias: string }>('/api/profile/alias', { method: 'POST', headers, body: { alias: name } })
      alias.value = res.alias
      renameAt.value = Date.now() + 7 * 86_400_000
      pickerOpen.value = false
      return null
    } catch (error) {
      const code = refusalCode(error)
      if (code) return code as AliasError
      console.warn('Alias save failed.', error)
      return 'offline'
    }
  }

  /** 🏷️🖼️ v6.45: wear a title / pick an avatar. Null clears. */
  async function setIdentity(patch: { title?: string | null; avatar?: string | null }): Promise<IdentityError | null> {
    const headers = await authed()
    if (!headers) return 'offline'
    try {
      const res = await apiFetch<{ ok: boolean; title: string | null; avatar: string | null }>('/api/profile/identity', { method: 'POST', headers, body: patch })
      title.value = res.title
      avatar.value = res.avatar
      return null
    } catch (error) {
      const code = refusalCode(error)
      if (code === 'unknown' || code === 'locked') return code
      console.warn('Identity save failed.', error)
      return 'offline'
    }
  }

  function dismiss() {
    dismissed.value = true
    pickerOpen.value = false
  }

  function openPicker() {
    pickerOpen.value = true
  }

  function onSignedOut() {
    alias.value = undefined
    renameAt.value = 0
    title.value = null
    avatar.value = null
    pickerOpen.value = false
  }

  /** 🚩 Flag a name on a board. Fire-and-forget; the server dedupes. */
  async function report(uid: string, board: 'run' | 'puzzle' | 'gauntlet' | 'weekly' | 'achievements'): Promise<boolean> {
    const headers = await authed()
    if (!headers) return false
    try {
      await apiFetch('/api/report', { method: 'POST', headers, body: { uid, board } })
      return true
    } catch (error) {
      console.warn('Report failed.', error)
      return false
    }
  }

  return { alias, renameAt, title, avatar, pickerOpen, load, afterSignIn, save, setIdentity, dismiss, openPicker, onSignedOut, report }
}
